/**
 * useRecentDocuments — as quatro origens que a home ainda não tinha.
 *
 * Os ORÇAMENTOS não são buscados aqui: a home já lê `quotes` inteiro para o
 * "A receber" do hero, e o chamador repassa essa lista. Buscar de novo seria uma
 * segunda consulta à mesma tabela — e, pior, a home poderia mostrar um orçamento
 * na lista que o número do hero ainda não conhece (ou o contrário) durante a
 * janela entre as duas respostas.
 *
 * Decisões que espelham o `useWork`, pelas mesmas razões:
 *
 * 1. **Offline-first de verdade.** `initialData` monta a lista dos caches que o
 *    `OfflineDataCacheWarmup` já aquece e `networkMode: 'offlineFirst'` deixa a
 *    query rodar sem rede em vez de ficar pausada (#41).
 * 2. **`initialDataUpdatedAt: 0` é obrigatório.** Esta query nasce DESABILITADA
 *    (`user`/`activeCompany` resolvem depois da montagem), então `refetchOnMount`
 *    já passou quando ela habilita; sem o carimbo, o cache semeado conta como
 *    FRESCO e a PRIMEIRA busca nunca sai — um recibo emitido em outro aparelho
 *    ficaria invisível, sem erro nenhum na tela (#158).
 * 3. **`safe()` em volta de cada promessa.** Sem rede o cliente do Supabase
 *    REJEITA (não devolve `{ error }`), e uma rejeição dentro de `Promise.all`
 *    descarta as outras respostas — a lista inteira sumiria por causa de uma
 *    tabela (#58).
 * 4. **NFS-e não tem cache offline** (o módulo fiscal é online-only por decisão
 *    de produto): sem rede a nota simplesmente não aparece na lista, nunca com
 *    informação errada. Mesmo contrato do `useWork`.
 *
 * Sem gate de PLANO de propósito: contrato e NFS-e são PRO para EMITIR, mas um
 * documento que já existe tem de aparecer para o dono dele — é o mesmo
 * raciocínio da trilha do trabalho, que mostra as cinco etapas para todo mundo.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import {
  getCachedContracts,
  getCachedReceipts,
  getCachedServiceOrders,
} from '@/lib/offlineDataCache';
import { CONTRACT_COLUMNS, NFSE_COLUMNS, ORDER_COLUMNS, RECEIPT_COLUMNS } from '@/hooks/useWork';
import {
  buildRecentDocuments,
  RECENT_DOCUMENTS_LIMIT,
  type RecentDocumentSources,
} from '@/lib/recentDocuments';
import type { ClientTimelineEntry } from '@/lib/clientSummary';

const RECENT_DOCUMENTS_KEY = 'recent-documents';

/**
 * Quantas linhas pedir por tabela.
 *
 * Folga deliberada sobre o que a tela mostra: para saber os 5 mais recentes do
 * conjunto basta ter os 5 mais recentes de cada origem, e o dobro cobre com
 * sobra o desencontro entre a coluna que ORDENA no servidor (`created_at`) e a
 * que a lista EXIBE (o recibo usa `issue_date`, que o usuário informa e pode
 * ser anterior). Não é corte silencioso: nenhum documento que caberia na tela
 * fica de fora por causa deste teto.
 */
const PER_TABLE_LIMIT = Math.max(RECENT_DOCUMENTS_LIMIT * 2, 10);

type Pool = Omit<RecentDocumentSources, 'quotes'>;

const EMPTY_POOL: Pool = {
  serviceOrders: [],
  contracts: [],
  receipts: [],
  nfseDocuments: [],
};

function poolFromCache(userId: string): Pool {
  return {
    serviceOrders: getCachedServiceOrders(userId) ?? [],
    contracts: getCachedContracts(userId) ?? [],
    receipts: getCachedReceipts(userId) ?? [],
    // Sem cache offline: o módulo fiscal é online-only.
    nfseDocuments: [],
  };
}

/** Uma criação offline ainda não sincronizada carrega id temporário (§6). */
const isOfflineRow = (row: any): boolean => String(row?.id ?? '').startsWith('offline_');

/**
 * Junta o que o servidor devolveu com as criações offline que ainda estão na
 * fila. Sem isto, a OS que o prestador acabou de criar sem rede DESAPARECE da
 * home assim que o sinal volta e a primeira busca responde — o dado não se
 * perde (está na fila), mas a tela diz que ele não existe.
 */
function mergePending(serverRows: any[], cachedRows: any[]): any[] {
  const pending = cachedRows.filter(isOfflineRow);
  if (pending.length === 0) return serverRows;
  const seen = new Set(serverRows.map((row) => String(row?.id)));
  return [...serverRows, ...pending.filter((row) => !seen.has(String(row?.id)))];
}

export interface UseRecentDocumentsResult {
  documents: ClientTimelineEntry[];
  isLoading: boolean;
}

/**
 * @param quotes Os orçamentos já carregados pela home (inclui vendas de balcão).
 */
export function useRecentDocuments(
  quotes: any[],
  limit: number = RECENT_DOCUMENTS_LIMIT,
): UseRecentDocumentsResult {
  const { user } = useAuth();
  const { activeCompany } = useCompany();

  const userId = user?.id ?? null;
  const companyId = activeCompany?.id ?? null;

  const query = useQuery({
    queryKey: [RECENT_DOCUMENTS_KEY, companyId],
    enabled: Boolean(userId && companyId),
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // Ver a decisão 2 no cabeçalho — sem isto a primeira busca nunca acontece.
    initialDataUpdatedAt: 0,
    initialData: (): Pool | undefined => (userId ? poolFromCache(userId) : undefined),
    queryFn: async (): Promise<Pool> => {
      if (!userId || !companyId) return EMPTY_POOL;

      const safe = <T,>(p: PromiseLike<T>): Promise<T | { data: null; error: true }> =>
        Promise.resolve(p).then(
          (r) => r,
          () => ({ data: null, error: true as const }),
        );

      const scoped = (table: string, columns: string) =>
        supabase
          .from(table as any)
          .select(columns)
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .limit(PER_TABLE_LIMIT);

      const [orders, contracts, receipts, nfse] = await Promise.all([
        safe(scoped('service_orders', ORDER_COLUMNS)),
        safe(scoped('contracts', CONTRACT_COLUMNS)),
        safe(scoped('receipts', RECEIPT_COLUMNS)),
        // Exclusão de nota é LÓGICA (`discarded_at`), nunca física.
        safe(scoped('nfse_documents', NFSE_COLUMNS).is('discarded_at', null)),
      ]);

      const cached = poolFromCache(userId);
      /** Origem que falhou cai no cache — nunca vira "não existe documento". */
      const rowsOf = (result: any, fallback: any[]): any[] =>
        result?.error || !result?.data ? fallback : (result.data as any[]);

      return {
        serviceOrders: mergePending(rowsOf(orders, cached.serviceOrders ?? []), cached.serviceOrders ?? []),
        contracts: mergePending(rowsOf(contracts, cached.contracts ?? []), cached.contracts ?? []),
        receipts: mergePending(rowsOf(receipts, cached.receipts ?? []), cached.receipts ?? []),
        nfseDocuments: rowsOf(nfse, []),
      };
    },
  });

  const documents = useMemo(
    () => buildRecentDocuments({ quotes, ...(query.data ?? EMPTY_POOL) }, limit),
    [quotes, query.data, limit],
  );

  return { documents, isLoading: query.isLoading };
}
